import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { forkJoin } from 'rxjs';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { ServicoTomadoService } from '../../../../../shared/services/apis/repo-dfse/servico-tomado.service';
import { ContasPagarService } from '../../../../../shared/services/apis/financas/contas-pagar.service';
import { ConfiguracaoCampoObrigatorioService } from '../../../../../shared/services/apis/financas/configuracao-campo-obrigatorio.service';
import { ServicoTomadoContasService } from '../../../../../shared/services/apis/orquestrador-financas/servico-tomado-contas.service';
import { ServicoTomadoDetalheStruct } from '../../../../../shared/services/structs/repo-dfse/servico-tomado-detalhe.struct';
import { CategoriaFinanceira } from '../../../../../shared/services/models/financas/categoria-financeira.model';
import { FormaPagamento } from '../../../../../shared/services/models/financas/forma-pagamento.model';
import { ContaFinanceira } from '../../../../../shared/services/models/financas/conta-financeira.model';
import { CentroCusto } from '../../../../../shared/services/models/financas/centro-custo.model';
import { TipoParcelamento } from '../../../../../shared/services/models/financas/tipo-parcelamento.model';
import { TelaFinancasEnum } from '../../../../../shared/enums/financas/tela-financas.enum';
import { GetConfiguracaoCampoObrigatorioResponse } from '../../../../../shared/services/responses/financas/configuracao-campo-obrigatorio.response';

export interface LancarFinanceiroDialogData {
  idNFSeEntrada: number;
}

@Component({
  selector: 'app-lancar-financeiro-dialog',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatIconModule,
    MatProgressSpinnerModule, MatTableModule, SelectPesquisavelComponent, AbntPipe, AlertCardComponent
  ],
  templateUrl: './lancar-financeiro-dialog.component.html',
  styleUrls: ['./lancar-financeiro-dialog.component.css'],
})
export class LancarFinanceiroDialogComponent implements OnInit {
  public isLoading = true;
  public isSaving = false;

  public detalhe: ServicoTomadoDetalheStruct | null = null;
  public form!: FormGroup;

  public listaCategorias: CategoriaFinanceira[] = [];
  public listaFormasPagamento: FormaPagamento[] = [];
  public listaContasFinanceiras: ContaFinanceira[] = [];
  public listaCentrosCusto: CentroCusto[] = [];
  private listaTiposParcelamento: TipoParcelamento[] = [];

  // Controlam o asterisco no rótulo: precisam acompanhar os validators aplicados a partir da
  // configuração da empresa, senão o campo exige preenchimento sem sinalizar que exige.
  public isCentroCustoObrigatorio = false;
  public isFormaPagamentoObrigatoria = false;
  public isContaFinanceiraObrigatoria = false;
  public displayedColumns = ['numero', 'vencimento', 'valor'];

  /** Impede o lançamento quando a nota está inconsistente; a mensagem diz o que corrigir. */
  public erroBloqueante: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<LancarFinanceiroDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LancarFinanceiroDialogData,
    private formBuilder: FormBuilder,
    private servicoTomadoService: ServicoTomadoService,
    private contasPagarService: ContasPagarService,
    private configuracaoCampoObrigatorioService: ConfiguracaoCampoObrigatorioService,
    private servicoTomadoContasService: ServicoTomadoContasService,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      idCategoriaFinanceira: [null, Validators.required],
      idCentroCusto: [null],
      idFormaPagamento: [null],
      idContaFinanceira: [null],
    });

    forkJoin({
      nota: this.servicoTomadoService.getById(this.data.idNFSeEntrada),
      lookups: this.contasPagarService.getLookups(),
      camposObrigatorios: this.configuracaoCampoObrigatorioService.getByTela(TelaFinancasEnum.ContasPagar),
    }).subscribe({
      next: ({ nota, lookups, camposObrigatorios }) => {
        this.isLoading = false;

        if (nota.isError || !nota.detalhe) {
          this.erroBloqueante = nota.errorDescription || 'Não foi possível carregar o serviço tomado.';
          return;
        }
        this.detalhe = nota.detalhe;

        if (!lookups.isError) {
          this.listaCategorias = lookups.categoriasFinanceiras || [];
          this.listaFormasPagamento = lookups.formasPagamento || [];
          this.listaContasFinanceiras = lookups.contasFinanceiras || [];
          this.listaCentrosCusto = lookups.centrosCusto || [];
          this.listaTiposParcelamento = lookups.tiposParcelamento || [];
        }

        this.aplicarCamposObrigatorios(camposObrigatorios);
        this.preSelecionarCategoria();
        this.validarNota();
      },
      error: () => {
        this.isLoading = false;
        this.erroBloqueante = 'Falha ao carregar os dados para o lançamento.';
      }
    });
  }

  /**
   * Espelha a obrigatoriedade configurada por empresa na tela de Contas a Pagar, para o
   * lançamento pela nota exigir os mesmos campos que o cadastro manual exigiria.
   */
  private aplicarCamposObrigatorios(response: GetConfiguracaoCampoObrigatorioResponse): void {
    const campos = (response?.listaTelaComCampos || []).flatMap(t => t.listaCampos || []);
    const isObrigatorio = (nomeTecnico: string) =>
      campos.some(c => c.nomeTecnico === nomeTecnico && c.isObrigatorio);

    if (isObrigatorio('id_forma_pagamento')) {
      this.isFormaPagamentoObrigatoria = true;
      this.form.get('idFormaPagamento')?.addValidators(Validators.required);
      this.form.get('idFormaPagamento')?.updateValueAndValidity();
    }
    if (isObrigatorio('id_conta_financeira')) {
      this.isContaFinanceiraObrigatoria = true;
      this.form.get('idContaFinanceira')?.addValidators(Validators.required);
      this.form.get('idContaFinanceira')?.updateValueAndValidity();
    }
    if (isObrigatorio('id_centro_custo')) {
      this.isCentroCustoObrigatorio = true;
      this.form.get('idCentroCusto')?.addValidators(Validators.required);
      this.form.get('idCentroCusto')?.updateValueAndValidity();
    }
  }

  /**
   * A categoria escolhida na nota só é aproveitada se for aceita pelo Contas a Pagar: lá o
   * lookup traz apenas folhas do tipo Despesa, enquanto o da nota traz níveis 1 e 2 de qualquer
   * tipo. Se não constar na lista, o usuário escolhe uma válida.
   */
  private preSelecionarCategoria(): void {
    const idCategoriaNota = this.detalhe?.idCategoriaFinanceira;
    if (!idCategoriaNota) return;

    const compativel = this.listaCategorias.some(c => c.idCategoriaFinanceira === idCategoriaNota);
    if (compativel) {
      this.form.get('idCategoriaFinanceira')?.setValue(idCategoriaNota);
    }
  }

  /** Repete no cliente as checagens do orquestrador, para avisar antes de gastar uma ida ao servidor. */
  private validarNota(): void {
    const d = this.detalhe;
    if (!d) return;

    if (!d.idPessoa) {
      this.erroBloqueante = 'Este serviço tomado não possui fornecedor vinculado. Edite a nota e informe o emitente antes de lançar o financeiro.';
      return;
    }
    if (!d.parcelas?.length) {
      this.erroBloqueante = 'Este serviço tomado não possui parcelas. Edite a nota e gere as parcelas antes de lançar o financeiro.';
      return;
    }
    if (!this.valorLiquido) {
      this.erroBloqueante = 'Este serviço tomado não possui valor líquido para lançar.';
      return;
    }
    if (Math.abs(this.somaParcelas - this.valorLiquido) > 0.01) {
      this.erroBloqueante = `A soma das parcelas não confere com o valor líquido da nota. Corrija as parcelas antes de lançar.`;
      return;
    }
    if (!this.tipoParcelamentoResolvido) {
      this.erroBloqueante = `Não há um tipo de parcelamento cadastrado para ${d.parcelas.length} parcela(s). Cadastre-o em Contas a Pagar antes de lançar.`;
      return;
    }
  }

  public get valorLiquido(): number {
    return Number(this.detalhe?.valorLiquido) || 0;
  }

  public get somaParcelas(): number {
    return (this.detalhe?.parcelas || []).reduce((soma, p) => soma + (Number(p.valorParcela) || 0), 0);
  }

  /** O Contas a Pagar exige um tipo de parcelamento cuja quantidade bata com a da nota. */
  private get tipoParcelamentoResolvido(): TipoParcelamento | undefined {
    const qtd = this.detalhe?.parcelas?.length || 0;
    return this.listaTiposParcelamento.find(t => t.quantidadeParcelas === qtd);
  }

  public confirmar(): void {
    if (this.erroBloqueante) return;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Preencha os campos obrigatórios.', AlertType.warning);
      return;
    }

    const tipoParcelamento = this.tipoParcelamentoResolvido;
    if (!tipoParcelamento) return;

    this.isSaving = true;
    const v = this.form.getRawValue();

    this.servicoTomadoContasService.lancar({
      idNFSeEntrada: this.data.idNFSeEntrada,
      idCategoriaFinanceira: v.idCategoriaFinanceira,
      idCentroCusto: v.idCentroCusto,
      idFormaPagamento: v.idFormaPagamento,
      idContaFinanceira: v.idContaFinanceira,
      idTipoParcelamento: tipoParcelamento.idTipoParcelamento,
    }).subscribe({
      next: (res) => {
        this.isSaving = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }
        if (res.avisoDescricao) {
          this.alertService.show('Atenção', res.avisoDescricao, AlertType.warning);
        } else {
          this.alertService.show('Sucesso', 'Financeiro lançado com sucesso.', AlertType.success);
        }
        this.dialogRef.close(true);
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Erro', 'Falha ao lançar o financeiro.', AlertType.error);
      }
    });
  }

  public cancelar(): void {
    this.dialogRef.close(false);
  }
}
